/**
 * BlockReportModal — allows users to block or report another user.
 * App Store compliance: provides clear moderation path.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  Alert,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { X, ShieldOff, Flag } from 'lucide-react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { TYPO } from '@/constants/typography';

const BLOCK_LIST_KEY = '@duel_blocked_users';

const REPORT_REASONS = [
  'Inappropriate content',
  'Fake lifts / cheating',
  'Harassment',
  'Spam',
  'Other',
] as const;

type ReportReason = (typeof REPORT_REASONS)[number];

interface BlockReportModalProps {
  visible: boolean;
  onClose: () => void;
  targetUserId: string;
  targetDisplayName?: string;
}

async function getBlockList(): Promise<string[]> {
  try {
    const raw = await AsyncStorage.getItem(BLOCK_LIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

async function addToBlockList(userId: string): Promise<void> {
  const list = await getBlockList();
  if (!list.includes(userId)) {
    list.push(userId);
    await AsyncStorage.setItem(BLOCK_LIST_KEY, JSON.stringify(list));
  }
}

export async function isUserBlocked(userId: string): Promise<boolean> {
  const list = await getBlockList();
  return list.includes(userId);
}

export async function unblockUser(userId: string): Promise<void> {
  const list = await getBlockList();
  const filtered = list.filter((id) => id !== userId);
  await AsyncStorage.setItem(BLOCK_LIST_KEY, JSON.stringify(filtered));
}

export default function BlockReportModal({
  visible,
  onClose,
  targetUserId,
  targetDisplayName,
}: BlockReportModalProps) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const c = theme.colors;

  const [mode, setMode] = useState<'menu' | 'report'>('menu');
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setMode('menu');
    setSelectedReason(null);
    setDetails('');
    onClose();
  };

  const handleBlock = async () => {
    setSubmitting(true);
    try {
      // Local blocklist
      await addToBlockList(targetUserId);

      // Record in supabase (best-effort)
      if (user?.id) {
        await supabase.from('user_blocks').upsert(
          { blocker_id: user.id, blocked_id: targetUserId },
          { onConflict: 'blocker_id,blocked_id' },
        ).then(() => {});
      }

      const msg = `${targetDisplayName || 'User'} has been blocked. You won't see their content.`;
      if (Platform.OS === 'web') {
        window.alert(msg);
      } else {
        Alert.alert('Blocked', msg);
      }
      resetAndClose();
    } catch {
      const errMsg = 'Failed to block user. Please try again.';
      if (Platform.OS === 'web') window.alert(errMsg);
      else Alert.alert('Error', errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async () => {
    if (!selectedReason) return;
    setSubmitting(true);
    try {
      if (user?.id) {
        const { error } = await supabase.from('user_reports').insert({
          profile_id: user.id,
          reported_id: targetUserId,
          reason: selectedReason,
          details: details.trim() || null,
        });
        if (error) throw error;
      }

      const msg = 'Report submitted. We\'ll review it shortly.';
      if (Platform.OS === 'web') window.alert(msg);
      else Alert.alert('Reported', msg);
      resetAndClose();
    } catch {
      const errMsg = 'Failed to submit report. Please try again.';
      if (Platform.OS === 'web') window.alert(errMsg);
      else Alert.alert('Error', errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={resetAndClose}>
      <View style={styles.overlay}>
        <View style={[styles.card, { backgroundColor: c.surface }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text }]}>
              {mode === 'menu' ? (targetDisplayName || 'User') : 'Report User'}
            </Text>
            <TouchableOpacity onPress={resetAndClose} hitSlop={12}>
              <X size={22} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          {mode === 'menu' ? (
            <View style={styles.menuOptions}>
              <TouchableOpacity
                style={[styles.menuBtn, { borderColor: c.border }]}
                onPress={handleBlock}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator color={c.error} />
                ) : (
                  <>
                    <ShieldOff size={20} color={c.error} />
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.menuBtnTitle, { color: c.text }]}>Block User</Text>
                      <Text style={[styles.menuBtnDesc, { color: c.textSecondary }]}>
                        Hide their content and prevent interactions
                      </Text>
                    </View>
                  </>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.menuBtn, { borderColor: c.border }]}
                onPress={() => setMode('report')}
              >
                <Flag size={20} color={c.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.menuBtnTitle, { color: c.text }]}>Report User</Text>
                  <Text style={[styles.menuBtnDesc, { color: c.textSecondary }]}>
                    Report inappropriate behavior or content
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.reportForm}>
              <Text style={[styles.reasonLabel, { color: c.textSecondary }]}>
                Select a reason:
              </Text>
              {REPORT_REASONS.map((reason) => (
                <TouchableOpacity
                  key={reason}
                  style={[
                    styles.reasonBtn,
                    {
                      borderColor: selectedReason === reason ? c.accent : c.border,
                      backgroundColor: selectedReason === reason ? c.accent + '15' : 'transparent',
                    },
                  ]}
                  onPress={() => setSelectedReason(reason)}
                >
                  <Text
                    style={[
                      styles.reasonText,
                      { color: selectedReason === reason ? c.accent : c.text },
                    ]}
                  >
                    {reason}
                  </Text>
                </TouchableOpacity>
              ))}

              <TextInput
                style={[
                  styles.detailsInput,
                  { backgroundColor: c.surfaceVariant, borderColor: c.border, color: c.text },
                ]}
                placeholder="Additional details (optional)"
                placeholderTextColor={c.textTertiary}
                multiline
                numberOfLines={3}
                value={details}
                onChangeText={setDetails}
                textAlignVertical="top"
              />

              <View style={styles.reportActions}>
                <TouchableOpacity
                  style={[styles.cancelBtn, { borderColor: c.border }]}
                  onPress={() => setMode('menu')}
                >
                  <Text style={[styles.cancelBtnText, { color: c.textSecondary }]}>Back</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.submitBtn,
                    {
                      backgroundColor: selectedReason ? c.accent : c.surfaceVariant,
                      opacity: selectedReason ? 1 : 0.5,
                    },
                  ]}
                  onPress={handleReport}
                  disabled={!selectedReason || submitting}
                >
                  {submitting ? (
                    <ActivityIndicator color={c.accentForeground} />
                  ) : (
                    <Text style={[styles.submitBtnText, { color: selectedReason ? c.accentForeground : c.textTertiary }]}>
                      Submit Report
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    borderRadius: 2,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontFamily: TYPO.heading.bold,
  },
  menuOptions: {
    gap: 10,
  },
  menuBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
  },
  menuBtnTitle: {
    fontSize: 16,
    fontFamily: TYPO.body.semibold,
  },
  menuBtnDesc: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    marginTop: 2,
  },
  reportForm: {
    gap: 8,
  },
  reasonLabel: {
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
    marginBottom: 4,
  },
  reasonBtn: {
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  reasonText: {
    fontSize: 14,
    fontFamily: TYPO.body.regular,
  },
  detailsInput: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    minHeight: 70,
    marginTop: 4,
  },
  reportActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 8,
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 0,
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
  },
  submitBtn: {
    flex: 1,
    borderRadius: 0,
    paddingVertical: 12,
    alignItems: 'center',
  },
  submitBtnText: {
    fontSize: 14,
    fontFamily: TYPO.body.bold,
  },
});
